import { useCallback, useState } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Badge,
    Box,
    IconButton,
    List,
    SvgIcon,
    Typography,
} from "@mui/material";
import { BRAND_NAME, ROUTES, WORDPRESS_URL } from "@constants";
import { useDeviceType } from "@hooks/useDeviceType";
import { useHeader } from "./hook/useHeader";
import { NavigationMobile } from "./components/NavigationMobile";
import { useStyle } from "./style";
import logoImg from "@static/img/header/Logo.svg";
import profileMobileImg from "@static/img/header/Profile Mobile.svg";
import cartImg from "@static/img/header/Cart.svg";
import xHeader from "@static/img/header/X Header.svg";
import MenuToggler from "@static/img/header/Menu Toggler.svg";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { BUTTON_IDS } from "@features/dataLayer/buttonIds";
import { keycloakUtils } from "itsf-ui-common";

interface IParams {
    cartItemsNumber?: number;
    logoutCallback?(): void;
}

export const HeaderMobile = (props: IParams) => {
    const { cartItemsNumber, logoutCallback } = props;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const { pathname } = useLocation();
    const { t } = useTranslation("header");
    const { isDesktop } = useDeviceType();
    const { classes } = useStyle({ isDesktop });
    const { handleCartClick, handleLoginLogout } = useHeader({ logoutCallback });

    const handleCloseMenu = useCallback(() => setIsMenuOpen(false), []);

    return (
        <Accordion
            className={classes.mobileAccordion}
            expanded={isMenuOpen}
            onChange={(_, expanded) => setIsMenuOpen(expanded)}
        >
            <AccordionSummary className={classes.accordionSummary} expandIcon={null}>
                <div className={classes.accordionHeader}>
                    <div className={classes.accordionHeaderLeft}>
                        {isMenuOpen ? (
                            <IconButton
                                aria-label={t("closeMenu")}
                                className={`${classes.drawerCloseButton} ${classes.menuToggleButton}`}
                                size="large"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCloseMenu();
                                }}
                            >
                                <SvgIcon className={classes.closeIcon} component={xHeader} viewBox="0 0 27 27" />
                            </IconButton>
                        ) : (
                            <IconButton
                                aria-label={t("openMenu")}
                                className={`${classes.drawerCloseButton} ${classes.menuToggleButton}`}
                                size="large"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsMenuOpen(true);
                                }}
                            >
                                <SvgIcon className={classes.menuIcon} component={MenuToggler} viewBox="0 0 32 25" />
                            </IconButton>
                        )}
                    </div>
                    <div className={classes.accordionHeaderCenter}>
                        <a aria-label={t("homePage")} href={WORDPRESS_URL} id={BUTTON_IDS.TOP_NAV.HOME_PAGE_LOGO}>
                            <SvgIcon
                                className={`${classes.drawerLogo} ${classes.logoMobile}`}
                                component={logoImg}
                                viewBox="0 0 80 46"
                            />
                        </a>
                    </div>
                    <div className={classes.accordionHeaderRight}>
                        <IconButton
                            aria-label={t("profile")}
                            className={classes.drawerCloseButton}
                            id={BUTTON_IDS.TOP_NAV[keycloakUtils.isAuthenticated() ? "LOGOUT" : "LOGIN"]}
                            size="large"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleLoginLogout();
                            }}
                        >
                            <SvgIcon
                                className={classes.profileMobileIcon}
                                component={profileMobileImg}
                                viewBox="0 0 28 28"
                            />
                        </IconButton>
                        {pathname !== `${ROUTES.CONFIRMATION_PAGE_URL}` && (
                            <Box
                                aria-label={t("cart")}
                                className={classes.cartContainer}
                                id={BUTTON_IDS.TOP_NAV.CART_ICON}
                                role="button"
                                tabIndex={0}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCartClick();
                                }}
                            >
                                <Badge
                                    badgeContent={cartItemsNumber}
                                    className={classes.badge}
                                    invisible={!cartItemsNumber}
                                >
                                    <SvgIcon
                                        className={classes.cartIconMobile}
                                        component={cartImg}
                                        viewBox="0 0 33 30"
                                    />
                                </Badge>
                            </Box>
                        )}
                    </div>
                </div>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
                <List className={classes.drawerMenuList}>
                    <NavigationMobile onLoginLogout={handleLoginLogout} onNavigate={handleCloseMenu} />
                </List>
                <div className={classes.drawerMyAccountButton}>
                    <LabeledButton
                        className={classes.myAccountButton}
                        href={ROUTES.MYACCOUNT_URL}
                        id={BUTTON_IDS.TOP_NAV.MY_GOMO}
                        labelText={t("myAccount", { brand: BRAND_NAME })}
                        onClick={handleCloseMenu}
                    >
                        <Typography className={classes.myAccountButtonText} variant="normal">
                            {t("myAccount", { brand: BRAND_NAME })}
                        </Typography>
                    </LabeledButton>
                </div>
            </AccordionDetails>
        </Accordion>
    );
};
